import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authAPI, leaderboardAPI, getApiBaseUrlWithoutApi } from '../config/api'

interface User {
  id: string | number
  name: string
  email: string
  username?: string
  avatar?: string
  streak?: number
  points?: number
  daysRemaining?: number
  subscription_expiry_date?: string | Date
  level?: number
  exp_progress_percent?: number
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  register: (data: { name: string; email: string; password: string; password_confirm: string; phone?: string }) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Kiểm tra localStorage và fetch user info khi component mount
    // CHỈ fetch từ API nếu đã có user trong localStorage (đã login qua frontend)
    // Điều này tránh việc tự động đăng nhập với user từ Django admin session
    const initAuth = async () => {
      try {
        const savedUser = localStorage.getItem('user')
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'

        if (savedUser && isLoggedIn) {
          try {
            const userData = JSON.parse(savedUser)
            // QUAN TRỌNG: Set user ngay từ localStorage TRƯỚC KHI setLoading(false)
            // Điều này đảm bảo isAuthenticated = true ngay khi loading = false 
            setUser(userData)
            // Set loading = false ngay sau khi set user
            // Để Header có thể render đúng với isAuthenticated = true
            setLoading(false)

            // Thử refresh user từ API trong background (không blocking)
            // Nếu fail, vẫn giữ user từ localStorage
            refreshUser().catch(() => {
              // QUAN TRỌNG: Không clear user, giữ lại để tránh mất đăng nhập
              // User vẫn có thể sử dụng app với dữ liệu từ localStorage
            })
          } catch (error) {
            // Chỉ clear nếu parse lỗi (JSON không hợp lệ)
            localStorage.removeItem('user')
            localStorage.removeItem('isLoggedIn')
            localStorage.removeItem('loginTimestamp')
            setUser(null)
            setLoading(false)
          }
        } else {
          // Nếu không có user trong localStorage hoặc không có flag isLoggedIn
          // Điều này tránh việc tự động đăng nhập với session từ Django admin 
          if (!savedUser) {
            localStorage.removeItem('isLoggedIn')
            localStorage.removeItem('loginTimestamp')
          }
          setUser(null)
          setLoading(false)
        }
      } catch (error) {
        // QUAN TRỌNG: Vẫn thử giữ lại user từ localStorage nếu có
        // Chỉ clear nếu thực sự không thể parse hoặc không có user
        const savedUser = localStorage.getItem('user')
        const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'
        if (savedUser && isLoggedIn) {
          try {
            const userData = JSON.parse(savedUser)
            setUser(userData)
          } catch (parseError) {
            setUser(null)
            localStorage.removeItem('user')
            localStorage.removeItem('isLoggedIn')
            localStorage.removeItem('loginTimestamp')
          }
        } else {
          setUser(null)
        }
        setLoading(false)
      }
    }
    initAuth()
  }, [])

  const refreshUser = async () => {
    // Chỉ refresh nếu đã có user trong localStorage
    // Điều này đảm bảo chỉ refresh user đã login qua frontend, không phải từ admin session
    const savedUser = localStorage.getItem('user')
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true'

    if (!savedUser || !isLoggedIn) {
      // Nếu không có user trong localStorage hoặc không có flag isLoggedIn, không gọi API
      return
    }

    try {
      const savedUserData = JSON.parse(savedUser)
      const userData = await authAPI.getMe()

      // Kiểm tra xem user từ API có khớp với user trong localStorage không
      // Nếu không khớp (có thể là user từ admin session), không cập nhật nhưng KHÔNG clear
      if (String(userData.id) !== String(savedUserData.id)) {
        // KHÔNG clear user, giữ lại để tránh mất đăng nhập
        return
      }

      // Lấy thông tin ranking để có streak và points chính xác nhất
      let rankingData = null
      try {
        rankingData = await leaderboardAPI.getMyRanking()
      } catch (rankingError) {
        // Nếu không lấy được ranking, dùng dữ liệu từ getMe()
        // Error fetching ranking
      }

      // Tính số ngày còn lại trong tháng hiện tại
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth()
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate() // Số ngày trong tháng
      const currentDay = today.getDate()
      const daysRemainingInMonth = Math.max(0, daysInMonth - currentDay)

      // Xử lý avatar URL để đảm bảo hiển thị đúng
      const API_BASE_URL = getApiBaseUrlWithoutApi()
      let avatarUrl = userData.avatar
      if (avatarUrl && avatarUrl.startsWith('/media/')) {
        avatarUrl = `${API_BASE_URL}${avatarUrl}`
      } else if (avatarUrl && !avatarUrl.startsWith('http') && !avatarUrl.startsWith('/images/')) {
        avatarUrl = `${API_BASE_URL}${avatarUrl}`
      }
      const transformedUser: User = {
        id: userData.id,
        name: userData.name || userData.username || '',
        email: userData.email,
        avatar: avatarUrl,
        streak: rankingData?.streak ?? userData.streak ?? 0,
        points: rankingData?.points ?? userData.total_points ?? 0,
        daysRemaining: daysRemainingInMonth,
        subscription_expiry_date: userData.subscription_expiry_date,
        level: userData.level ?? 0,
        exp_progress_percent: userData.exp_progress_percent ?? 0,
      }
      setUser(transformedUser)
      // Cập nhật localStorage để giữ dữ liệu mới nhất
      localStorage.setItem('user', JSON.stringify(transformedUser))
    } catch (error: any) {
      // QUAN TRỌNG: KHÔNG BAO GIỜ clear user khi có lỗi trong refreshUser
      // Giữ lại user trong localStorage và state để tránh mất đăng nhập

      // KHÔNG clear user ở đây, giữ lại để tránh mất đăng nhập
      // User vẫn có thể sử dụng app với dữ liệu từ localStorage
    }
  }

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const userData = await authAPI.login({ email, password })

      // Kiểm tra tài khoản chưa được duyệt (backend trả về HTTP 200 với account_pending)
      if ((userData as any).account_pending) {
        return {
          success: false,
          pending: true,
          error: (userData as any).message || 'Tài khoản của bạn chưa được duyệt. Vui lòng chờ Admin xét duyệt.'
        } as any
      }

      // Backend dùng session-based authentication (cookies), không trả về token
      // Cookies sẽ tự động được gửi kèm với mọi request nhờ withCredentials: true

      // Kiểm tra cookies sau khi login
      // Đợi một chút để cookies được set từ response headers
      await new Promise(resolve => setTimeout(resolve, 200))

      // Lấy thông tin ranking để có streak và points chính xác nhất
      let rankingData = null
      try {
        rankingData = await leaderboardAPI.getMyRanking()
      } catch (rankingError) {
        // Nếu không lấy được ranking, dùng dữ liệu từ login response
        // Error fetching ranking
      }

      // Tính số ngày còn lại trong tháng hiện tại
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth()
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate() // Số ngày trong tháng
      const currentDay = today.getDate()
      const daysRemainingInMonth = Math.max(0, daysInMonth - currentDay)

      // Xử lý avatar URL để đảm bảo hiển thị đúng
      const API_BASE_URL = getApiBaseUrlWithoutApi()
      let avatarUrl = userData.avatar
      if (avatarUrl && avatarUrl.startsWith('/media/')) {
        avatarUrl = `${API_BASE_URL}${avatarUrl}`
      } else if (avatarUrl && !avatarUrl.startsWith('http') && !avatarUrl.startsWith('/images/')) {
        avatarUrl = `${API_BASE_URL}${avatarUrl}`
      }
      const transformedUser: User = {
        id: userData.id,
        name: userData.name || userData.username || '',
        email: userData.email,
        avatar: avatarUrl,
        streak: rankingData?.streak ?? userData.streak ?? 0,
        points: rankingData?.points ?? userData.total_points ?? 0,
        daysRemaining: daysRemainingInMonth,
        subscription_expiry_date: userData.subscription_expiry_date,
        level: userData.level ?? 0,
        exp_progress_percent: userData.exp_progress_percent ?? 0,
      }

      // Lưu user vào localStorage
      setUser(transformedUser)
      localStorage.setItem('user', JSON.stringify(transformedUser))

      // Lưu thêm flag để đánh dấu đã login thành công
      // Flag này giúp phân biệt user đã login qua frontend vs admin session
      localStorage.setItem('isLoggedIn', 'true')
      localStorage.setItem('loginTimestamp', Date.now().toString())

      return { success: true }
    } catch (error: any) {
      // Xử lý lỗi network
      if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
        return {
          success: false,
          error: 'Không thể kết nối đến server. Vui lòng kiểm tra:\n1. Backend server có đang chạy không?\n2. Kết nối mạng có ổn định không?'
        }
      }

      // Kiểm tra lỗi tài khoản chưa kích hoạt
      const nonFieldErrors = error.response?.data?.non_field_errors
      if (nonFieldErrors && Array.isArray(nonFieldErrors) && nonFieldErrors.length > 0) {
        return { success: false, error: nonFieldErrors[0] }
      }
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        (error.response?.data?.email ? `Email: ${error.response.data.email[0]}` : '') ||
        (error.response?.data?.password ? `Mật khẩu: ${error.response.data.password[0]}` : '') ||
        'Đăng nhập thất bại. Vui lòng kiểm tra lại email và mật khẩu.'
      return { success: false, error: errorMessage }
    }
  }

  const register = async (data: { name: string; email: string; password: string; password_confirm: string; phone?: string }): Promise<{ success: boolean; error?: string }> => {
    try {
      await authAPI.register({
        name: data.name,
        email: data.email,
        password: data.password,
        password_confirm: data.password_confirm,
        username: data.phone || data.email.split('@')[0], // Use phone or email prefix as username
      })
      // Không set user vì tài khoản chưa được kích hoạt, không thể login
      // Chỉ trả về success
      return { success: true }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message ||
        error.response?.data?.error ||
        (error.response?.data?.email ? `Email: ${error.response.data.email[0]}` : '') ||
        (error.response?.data?.password ? `Mật khẩu: ${error.response.data.password[0]}` : '') ||
        (error.response?.data?.password_confirm ? `Xác nhận mật khẩu: ${error.response.data.password_confirm[0]}` : '') ||
        'Đăng ký thất bại. Vui lòng thử lại.'
      return { success: false, error: errorMessage }
    }
  }

  const logout = async () => {
    try {
      await authAPI.logout()
    } catch (error) {
      // Ignore logout errors
    } finally {
      setUser(null)
      localStorage.removeItem('user')
      localStorage.removeItem('isLoggedIn')
      localStorage.removeItem('loginTimestamp')
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

